import {
  AwaitContainer,
  Button,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  ScrollArea,
} from '@ibsys/kit';
import type { UseQueryResult } from '@tanstack/react-query';
import { HistoryIcon } from 'lucide-react';
import { Fragment, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIALOG_CONTENT_SCREEN_BOUNDED, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { IconButton } from '../IconButton';

/**
 * Хук перечня записей истории: запрашивает перечень владельца `params`, пока `enabled`.
 */
export type HistoryQueryHook<P, T> = (params: P, enabled: boolean) => UseQueryResult<{ items: T[] }>;

type Props<P, T> = {
  /** Название истории: заголовок окна и подпись кнопки, которая его открывает */
  title: string;
  /** Текст на месте пустого перечня */
  emptyText: string;
  /** Хук перечня записей — окно зовёт его с `params` и признаком открытия */
  useHistory: HistoryQueryHook<P, T>;
  /** Владелец истории: параметры хука перечня */
  params: P;
  getItemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
};

/**
 * Кнопка со значком часов и окно истории изменений.
 *
 * Единственное место, где заданы вид такого окна и его обвязка: значок, запрос перечня по открытию,
 * прокрутка, ожидание, пустой перечень и закрытие. Потребитель называет историю, её хук и вид записи.
 */
export const HistoryDialog = <P, T>({ title, emptyText, useHistory, params, getItemKey, renderItem }: Props<P, T>) => {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  // Перечень нужен только открытому окну: история у каждого владельца своя, а смотрят её заметно
  // реже, чем сам владелец
  const { data, isLoading } = useHistory(params, isOpen);
  const items = data?.items ?? [];

  return (
    <DialogRoot open={isOpen} onOpenChange={setIsOpen}>
      <IconButton
        size="icon"
        className="size-5 align-middle"
        label={title}
        icon={<HistoryIcon className="size-3" />}
        trigger={DialogTrigger}
      />
      {/* Записей в истории копится сколько угодно */}
      <DialogContent className={DIALOG_CONTENT_SCREEN_BOUNDED}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <ScrollArea orientation="vertical" className={SCROLL_AREA_VIEWPORT_BLOCK}>
          <AwaitContainer isLoading={isLoading}>
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">{emptyText}</p>
            ) : (
              <div className="flex flex-col gap-3 text-sm">
                {items.map((item) => (
                  <Fragment key={getItemKey(item)}>{renderItem(item)}</Fragment>
                ))}
              </div>
            )}
          </AwaitContainer>
        </ScrollArea>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t('close')}</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
